// Central service-health state + DB-unavailable classification.
//
// During an Atlas no-primary / failover / capacity event the backend now returns
// a controlled 503 ({"error":"service_unavailable"}). This module gives the UI a
// single source of truth so it can: (1) show a "temporarily unavailable" banner,
// (2) back off polling, (3) never render fake "Online" / sample data as if live.

let _degraded = false;
let _lastReason = null;
const _subs = new Set();

function _emit() {
  for (const fn of _subs) {
    try { fn(_degraded, _lastReason); } catch (_) { /* listener errors must not break health */ }
  }
}

export function isDegraded() { return _degraded; }

export function subscribe(fn) {
  _subs.add(fn);
  // fire immediately with current state so late subscribers are correct
  try { fn(_degraded, _lastReason); } catch (_) {}
  return () => _subs.delete(fn);
}

export function markDegraded(reason = 'service_unavailable') {
  _lastReason = reason;
  if (!_degraded) { _degraded = true; _emit(); }
}

export function markHealthy() {
  if (_degraded) { _degraded = false; _lastReason = null; _emit(); }
}

// Classify an axios error (or fetch failure) as a transient backend/DB outage.
//   - HTTP 503 or 502/504 (infra)
//   - body { error: 'service_unavailable' }
//   - no response at all (network / CORS-block / DNS) → treat as outage, not auth
export function isServiceUnavailable(error) {
  if (!error) return false;
  const resp = error.response;
  if (!resp) return true; // network error / CORS block / timeout — backend unreachable
  const status = resp.status;
  if (status === 502 || status === 503 || status === 504) return true;
  const data = resp.data;
  if (data && typeof data === 'object' && data.error === 'service_unavailable') return true;
  return false;
}

// Exponential backoff with jitter, capped. attempt is 1-based.
export function backoffDelayMs(attempt, base = 600, cap = 8000) {
  const exp = Math.min(cap, base * Math.pow(3, attempt - 1));
  const jitter = Math.random() * 0.25 * exp;
  return Math.round(exp + jitter);
}

export function sleep(ms) { return new Promise((r) => setTimeout(r, ms)); }
